import React from 'react'

import {
  arrayOf,
  object,
  shape,
  string,
  bool,
} from 'prop-types'

import useIterableInAppMessages from 'Pods/Shared/Hooks/useIterableInAppMessages'
import usePageConfig from 'Pods/Shared/Hooks/usePageConfig'
import i18n from 'Pods/Shared/I18nHome'

import { EMPTY_ARRAY, EMPTY_OBJECT } from 'Utils/stableDefaults'

import HeroAuthor from './Components/HeroAuthor'
import Search from './Components/Search/Search'
import TrendingButtons from './Components/TrendingButtons'
import { HomeHeroSectionMock } from './HomeHeroSectionMock'
import { getImageType } from './Static/images'

function HomeHeroSection({
  images = EMPTY_OBJECT,
  locale = 'en',
  trending = EMPTY_ARRAY,
  extractingCoreStyles = false,
  author = null,
  searchDropdown = null,
}) {
  const pageConfig = usePageConfig()

  // The hero is the homepage's only always-rendered section, so it hosts the Iterable in-app mount.
  useIterableInAppMessages(pageConfig)

  const desktopImage = images?.desktop
  const desktopSmallImage = images?.desktopSmall || images?.desktop
  const tabletImage = images?.tablet || images?.desktopSmall || images?.desktop
  const mobileImage = images?.mobile
  const CreatedBy = i18n.t('hero.CreatedBy', {
    ns: 'new-home',
    lng: locale,
  })
  const heroTitle = i18n.t('hero.title', {
    ns: 'new-home',
    lng: locale,
  })
  const aiSearchPlaceholder = i18n.t('search.placeholder.aiMode', {
    ns: 'new-home',
    lng: locale,
  })
  const backgroundAlt = i18n.t('hero.backgroundAlt', {
    ns: 'new-home',
    lng: locale,
  })
  const trendingButtons = extractingCoreStyles ? HomeHeroSectionMock : trending

  return (
    <div className="home-hero home-hero--new-search">
      {author?.name && (
        <HeroAuthor createdByText={CreatedBy} author={author} />
      )}
      <picture className="home-hero__background">
        {[
          { media: '(min-width: 1600px)', src: desktopImage },
          { media: '(min-width: 960px)', src: desktopSmallImage },
          { media: '(min-width: 640px)', src: tabletImage },
        ].map(({ media, src }) => (
          <source
            key={media}
            media={media}
            srcSet={src}
            type={getImageType(src)}
          />
        ))}
        <img
          alt={backgroundAlt}
          className="home-hero__background-image"
          src={mobileImage}
          // eslint-disable-next-line react/no-unknown-property
          fetchpriority="high"
          loading="eager"
        />
      </picture>

      <div className="home-hero__content">
        <h1 className="home-hero__title">{heroTitle}</h1>

        <div className="home-hero__search">
          <Search
            placeholder={aiSearchPlaceholder}
            locale={locale}
            aiMode
            searchDropdown={searchDropdown}
          />

          {locale === 'en' && trendingButtons.length > 0 && Array.isArray(trendingButtons) && (
            <div className="home-hero__trending-buttons-container">
              <TrendingButtons
                buttons={trendingButtons}
                buttonSize="small"
                customClass="home-hero__trending-button"
                trackingContext="home-hero"
                locale={locale}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

const authorShape = shape({
  name: string,
  href: string,
  avatarUrl: string,
})

HomeHeroSection.propTypes = {
  images: object,
  locale: string,
  author: authorShape,
  trending: arrayOf(
    shape({
      label: string,
      href: string,
    })
  ),
  extractingCoreStyles: bool,
  searchDropdown: object,
}

export default HomeHeroSection
